import { Typography } from '@euler/app/components/typography';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { ConstructionJobGradientPanel } from '@euler/app/flows/task/pages/construction/components/ConstructionJobGradientPanel';
import { useConstructionJobResolveList } from '@euler/app/flows/task/pages/construction/functions/useConstructionJobResolveList';
import { Icon } from '@euler/components/Icomoon';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { ServiceSubjectType } from '@euler/model/enum';
import { uniq } from 'ramda';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { StyleProp, TouchableOpacity, ViewStyle } from 'react-native';

const Cell = memo(
  ({
    title,
    style,
    checked,
    editable,
    onPress,
  }: {
    title: string;
    checked?: boolean;
    editable?: boolean;
    style?: StyleProp<ViewStyle>;
    onPress?: (title: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    const onCellPress = useCallback(() => {
      onPress?.(title);
    }, [onPress, title]);

    return (
      <TouchableOpacity
        onPress={editable ? onCellPress : undefined}
        activeOpacity={editable ? undefined : 1}
        css={`
          height: ${rpx(40)}px;
          border-radius: ${rpx(5)}px;
          border-width: 1px;
          border-color: ${checked ? '#207FE7' : '#F1F1F1'};
          flex-direction: row;
          align-items: center;
          justify-content: space-between;
          padding: 0 ${rpx(12)}px;
        `}
        style={style}
      >
        <Typography.Label
          color={checked ? '#207FE7' : '#111'}
          light
          size={rpx(15)}
        >
          {title}
        </Typography.Label>
        {checked && <Icon name="checkbox" color="#207FE7" size={rpx(24)} />}
      </TouchableOpacity>
    );
  },
);

export const WiredConstructionJobResolveList = memo(
  ({
    jobName,
    editable,
    resolves,
    style,
    onItemPress,
  }: {
    jobName: string;
    editable?: boolean;
    resolves?: string[];
    style?: StyleProp<ViewStyle>;
    onItemPress?: (item: string) => void;
  }) => {
    const { rpx } = useResponsiveContext();
    const {
      detail: {
        basicInfo: { orderDetail },
      },
    } = useTaskContext();
    const state = useConstructionJobResolveList();
    const workItems = useMemo(
      () =>
        state.status === 'success'
          ? uniq(state.result!.map(x => x.maintenanceAdvice))
          : [],
      [state],
    );

    useEffect(() => {
      if (!resolves?.includes(jobName) && workItems.includes(jobName)) {
        onItemPress?.(jobName);
      }
    }, [workItems, jobName, onItemPress, resolves]);

    const items = useMemo(
      () => [
        ...workItems,
        ...(orderDetail?.serviceSubjects
          ?.filter(
            x =>
              x.type === ServiceSubjectType.Fault ||
              x.type === ServiceSubjectType.Other,
          )
          .map(x => x.name) ?? []),
      ],
      [workItems, orderDetail?.serviceSubjects],
    );

    if (!items.length) {
      return null;
    }

    return (
      <ConstructionJobGradientPanel
        title="该服务履行的项目"
        remark={editable ? '可多选，可不选' : undefined}
        style={style}
      >
        {items.map(item => (
          <Cell
            key={item}
            title={item}
            editable={editable}
            checked={resolves?.includes(item)}
            onPress={editable ? onItemPress : undefined}
            css={`
              margin-bottom: ${rpx(10)}px;
              margin-left: ${rpx(15)}px;
              margin-right: ${rpx(15)}px;
            `}
          />
        ))}
      </ConstructionJobGradientPanel>
    );
  },
);
